import React, { useCallback, useEffect, useRef } from "react";

const HOLD_MS = 500;
const MOVE_TOLERANCE_PX = 10;

/**
 * Touch press-and-hold. Moving more than 10px, lifting, or the browser taking
 * the gesture over (scrolling) cancels it. Once it fires, the gesture's own
 * touchend and click are swallowed, so the press opens nothing else.
 */
export function useLongPress(onLongPress: () => void) {
  const timer = useRef<number>();
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);

  const cancel = useCallback(() => {
    window.clearTimeout(timer.current);
    start.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  const handlers = {
    onPointerDown: (event: React.PointerEvent) => {
      // A second finger (pinch) cancels; only a lone touch or pen press counts.
      cancel();
      if (event.pointerType === "mouse" || event.isPrimary === false) return;
      fired.current = false;
      start.current = { x: event.clientX, y: event.clientY };
      timer.current = window.setTimeout(() => {
        start.current = null;
        fired.current = true;
        if ("vibrate" in navigator) navigator.vibrate(10);
        onLongPress();
      }, HOLD_MS);
    },
    onPointerMove: (event: React.PointerEvent) => {
      const origin = start.current;
      if (
        origin &&
        Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > MOVE_TOLERANCE_PX
      ) {
        cancel();
      }
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onContextMenu: (event: React.MouseEvent) => event.preventDefault(),
    onTouchEnd: (event: React.TouchEvent) => {
      if (fired.current) event.preventDefault();
    },
  };

  /** True (once) when the click being handled ends a long press. */
  const consumeLongPress = () => {
    const wasLongPress = fired.current;
    fired.current = false;
    return wasLongPress;
  };

  return { handlers, consumeLongPress };
}
